import { create } from 'zustand'
import type { UsersAndContacts } from '@/types/contacts'
import type { Transaction } from '@/domain/transactions'

interface SelectedRecipient {
  id: string
  name: string
  username?: string
  avatar?: string
  phoneNumber?: string
  avatarBg?: [string, string, ...string[]]
}

interface SplitTransactionState {
  transaction: Transaction | null
  selectedRecipients: SelectedRecipient[]
  maxRecipients: number
  
  // Actions
  setTransaction: (transaction: Transaction | null) => void
  addRecipient: (recipient: UsersAndContacts) => void
  removeRecipient: (recipientId: string) => void
  toggleRecipient: (recipient: UsersAndContacts) => void
  isRecipientSelected: (recipientId: string) => boolean
  clearSplit: () => void
  canAddMore: () => boolean
  
  // Calculations
  getAmountPerPerson: () => number
  getInitiatorAmount: () => number
  getTotalPeople: () => number
}

export const useSplitTransactionStore = create<SplitTransactionState>(
  (set, get) => ({
    transaction: null,
    selectedRecipients: [],
    maxRecipients: 10,

    setTransaction: (transaction: Transaction | null) => {
      set({ transaction, selectedRecipients: [] })
    },

    addRecipient: (recipient: UsersAndContacts) => {
      const { selectedRecipients, maxRecipients } = get()
      
      // Check if already selected
      if (selectedRecipients.some((r) => r.id === recipient.id)) {
        return
      }
      
      // Check max limit
      if (selectedRecipients.length >= maxRecipients) {
        return
      }

      const newRecipient: SelectedRecipient = {
        id: recipient.id,
        name: `${recipient.first_name} ${recipient.last_name}`.trim(),
        username: recipient.username,
        avatar: recipient.avatar_path,
        phoneNumber: recipient.phone_number,
        avatarBg: recipient.avatarBg,
      }

      set({
        selectedRecipients: [...selectedRecipients, newRecipient],
      })
    },

    removeRecipient: (recipientId: string) => {
      set((state) => ({
        selectedRecipients: state.selectedRecipients.filter(
          (r) => r.id !== recipientId,
        ),
      }))
    },

    toggleRecipient: (recipient: UsersAndContacts) => {
      const { selectedRecipients } = get()
      const isSelected = selectedRecipients.some((r) => r.id === recipient.id)

      if (isSelected) {
        get().removeRecipient(recipient.id)
      } else {
        get().addRecipient(recipient)
      }
    },

    isRecipientSelected: (recipientId: string) => {
      return get().selectedRecipients.some((r) => r.id === recipientId)
    },

    clearSplit: () => {
      set({ transaction: null, selectedRecipients: [] })
    },

    canAddMore: () => {
      const { selectedRecipients, maxRecipients } = get()
      return selectedRecipients.length < maxRecipients
    },

    // Calculate amount per person for equal split
    getAmountPerPerson: () => {
      const { transaction, selectedRecipients } = get()
      if (!transaction) return 0

      const totalAmount = Number(transaction.amount.amount)
      const totalPeople = selectedRecipients.length + 1 // +1 for initiator
      
      // Round down to 2 decimal places
      return Math.floor((totalAmount / totalPeople) * 100) / 100
    },

    // Calculate initiator's amount (absorbs remainder)
    getInitiatorAmount: () => {
      const { transaction, selectedRecipients } = get()
      if (!transaction) return 0

      const totalAmount = Number(transaction.amount.amount)
      const amountPerPerson = get().getAmountPerPerson()
      const totalForOthers = amountPerPerson * selectedRecipients.length
      
      // Initiator pays the remainder
      return totalAmount - totalForOthers
    },

    // Get total number of people (including initiator)
    getTotalPeople: () => {
      const { selectedRecipients } = get()
      return selectedRecipients.length + 1
    },
  }),
)

